import { apiFetch, ApiError } from '../apiFetch'
import type {
  SalesOrder,
  SalesOrderDetail,
  CreateSalesOrderInput,
  UpdateSalesOrderInput,
  SalesOrderFilter,
  SalesOrderListResult,
  VariantSearchResult,
  SalesOrderHistory,
} from './types'

async function parseResponse<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    const err = data?.error
    const code = err?.code ?? 'HTTP_ERROR'
    const message = err?.message ?? `${fallback} (${res.status})`
    const details = err?.details
    throw new ApiError(code, message, res.status, details)
  }
  return data as T
}

export async function listSalesOrdersApi(
  filter?: SalesOrderFilter,
): Promise<SalesOrderListResult> {
  const params = new URLSearchParams()
  if (filter?.customerId) params.set('customerId', filter.customerId)
  if (filter?.buyerId) params.set('buyerId', filter.buyerId)
  if (filter?.status && filter.status !== 'all') params.set('status', filter.status)
  const lineStatus = filter?.lineStatus || filter?.line_status
  if (lineStatus && lineStatus !== 'all') params.set('lineStatus', lineStatus)
  if (filter?.fromDate) params.set('fromDate', filter.fromDate)
  if (filter?.toDate) params.set('toDate', filter.toDate)
  if (filter?.q) params.set('q', filter.q)
  if (filter?.page !== undefined) params.set('page', String(filter.page))
  if (filter?.pageSize !== undefined) params.set('pageSize', String(filter.pageSize))

  const query = params.toString() ? `?${params.toString()}` : ''
  const res = await apiFetch(`/api/sales-orders${query}`)
  const raw = await parseResponse<SalesOrderListResult | SalesOrder[]>(
    res,
    'Không thể tải danh sách đơn hàng',
  )
  if (Array.isArray(raw)) {
    return {
      items: raw,
      total: raw.length,
      page: filter?.page ?? 1,
      pageSize: filter?.pageSize ?? raw.length,
    }
  }
  return raw
}

export async function getSalesOrderApi(id: string): Promise<SalesOrderDetail | null> {
  const res = await apiFetch(`/api/sales-orders/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  return parseResponse<SalesOrderDetail>(res, 'Không thể tải chi tiết đơn hàng')
}

export async function createSalesOrderApi(input: CreateSalesOrderInput): Promise<SalesOrderDetail> {
  const res = await apiFetch('/api/sales-orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return parseResponse<SalesOrderDetail>(res, 'Không thể tạo đơn hàng')
}

export async function patchSalesOrderApi(
  id: string,
  patch: UpdateSalesOrderInput,
): Promise<SalesOrderDetail> {
  const res = await apiFetch(`/api/sales-orders/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  return parseResponse<SalesOrderDetail>(res, 'Không thể cập nhật đơn hàng')
}

export async function updateSalesOrderLineStatusApi(
  orderId: string,
  lineId: string,
  input: { status: 'draft' | 'sx' | 'cancelled'; reason?: string },
): Promise<SalesOrderDetail> {
  const res = await apiFetch(
    `/api/sales-orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(lineId)}/status`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  )
  return parseResponse<SalesOrderDetail>(res, 'Không thể cập nhật trạng thái dòng đơn hàng')
}

export async function confirmSalesOrderApi(id: string): Promise<SalesOrderDetail> {
  const res = await apiFetch(`/api/sales-orders/${encodeURIComponent(id)}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  return parseResponse<SalesOrderDetail>(res, 'Không thể xác nhận đơn hàng')
}

export async function cancelSalesOrderApi(id: string, reason: string): Promise<SalesOrderDetail> {
  const res = await apiFetch(`/api/sales-orders/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  return parseResponse<SalesOrderDetail>(res, 'Không thể hủy đơn hàng')
}

export async function reopenSalesOrderApi(id: string, reason: string): Promise<SalesOrderDetail> {
  const res = await apiFetch(`/api/sales-orders/${encodeURIComponent(id)}/reopen`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  })
  return parseResponse<SalesOrderDetail>(res, 'Không thể khôi phục đơn hàng')
}

export async function getSalesOrderHistoryApi(id: string): Promise<SalesOrderHistory[]> {
  const res = await apiFetch(`/api/sales-orders/${encodeURIComponent(id)}/history`)
  return parseResponse<SalesOrderHistory[]>(res, 'Không thể tải lịch sử đơn hàng')
}

export async function searchVariantsApi(q?: string, limit = 100): Promise<VariantSearchResult[]> {
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  params.set('limit', String(Math.min(Math.max(1, limit), 100)))
  const res = await apiFetch(`/api/variants?${params.toString()}`)
  return parseResponse<VariantSearchResult[]>(res, 'Không thể tìm kiếm variant')
}
